/**
 * Expressions and poses of the cast (ART-BIBLE §6), built from the base figures in `cast.ts`:
 *   Scribble (fox kit): happy, curious, surprised, sleepy, proud, wave, thinking, cheer, point
 *   Teacher Juno:       smile, laugh, wow, thinking, proud, point, cheer
 * Only eyes / mouth / brows / arms / head tilt change, so every pose stays on-model.
 */
import type { FigureDef, Layer } from '../Figure';
import { ellipse, mirrorX, rotate } from '../geom';
import type { StyleId } from '../tokens';
import { juno, scribble } from './cast';

export type ScribbleMood = 'happy' | 'curious' | 'surprised' | 'sleepy' | 'proud' | 'wave' | 'thinking' | 'cheer' | 'point';
export const SCRIBBLE_POSES: ScribbleMood[] = ['happy', 'curious', 'surprised', 'sleepy', 'proud', 'wave', 'thinking', 'cheer', 'point'];
export type JunoFace = 'smile' | 'laugh' | 'wow' | 'thinking' | 'proud' | 'point' | 'cheer';
export const JUNO_POSES: JunoFace[] = ['smile', 'laugh', 'wow', 'thinking', 'proud', 'point', 'cheer'];

const M = (d: string) => mirrorX(d, 120);
const f = (n: number) => Math.round(n * 100) / 100;

/** Capsule (rounded limb) from a to b with radius r. */
function capsule(ax: number, ay: number, bx: number, by: number, r: number): string {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const nx = (-dy / len) * r;
  const ny = (dx / len) * r;
  const ux = (dx / len) * r * 1.33;
  const uy = (dy / len) * r * 1.33;
  return (
    `M${f(ax + nx)} ${f(ay + ny)}L${f(bx + nx)} ${f(by + ny)}` +
    `C${f(bx + nx + ux)} ${f(by + ny + uy)} ${f(bx - nx + ux)} ${f(by - ny + uy)} ${f(bx - nx)} ${f(by - ny)}` +
    `L${f(ax - nx)} ${f(ay - ny)}` +
    `C${f(ax - nx - ux)} ${f(ay - ny - uy)} ${f(ax + nx - ux)} ${f(ay + ny - uy)} ${f(ax + nx)} ${f(ay + ny)}Z`
  );
}

const sparkle = (x: number, y: number, r: number, fill = 'bell'): Layer => ({
  k: 'spot',
  d: `M${x} ${y - r}C${x + r * 0.18} ${y - r * 0.18} ${x + r * 0.18} ${y - r * 0.18} ${x + r} ${y}C${x + r * 0.18} ${y + r * 0.18} ${x + r * 0.18} ${y + r * 0.18} ${x} ${y + r}C${x - r * 0.18} ${y + r * 0.18} ${x - r * 0.18} ${y + r * 0.18} ${x - r} ${y}C${x - r * 0.18} ${y - r * 0.18} ${x - r * 0.18} ${y - r * 0.18} ${x} ${y - r}Z`,
  fill,
});

/* --------------------------------- Scribble --------------------------------- */

const isEye = (l: Layer) => l.k === 'spot' && (l.fill === 'eye' || (l.fill === 'white' && l.d.startsWith('M') && Number(l.d.slice(1).split(' ')[1]) < 112));
/** Head layers (tilted for 'curious'): everything from the ears on, except the arms added later. */
const HEAD_FROM = 12;

function happyEyes(y: number, open = 1): Layer[] {
  // closed "smiling" eyes ∩ (open = 1) or sleepy ∪ (open = -1)
  const arc = (cx: number) => `M${cx - 10} ${y + 3 * open}C${cx - 6} ${y - 7 * open} ${cx + 6} ${y - 7 * open} ${cx + 10} ${y + 3 * open}`;
  return [
    { k: 'ink', d: arc(98), w: 1.15 },
    { k: 'ink', d: arc(142), w: 1.15 },
  ];
}

const foxArm = (ax: number, ay: number, bx: number, by: number): Layer[] => [
  { k: 'region', d: capsule(ax, ay, bx, by, 9), fill: 'foxFur', shade: 3, rim: true },
  { k: 'region', d: ellipse(bx, by, 10.5, 10.5), fill: 'foxDark', shade: 3 },
];

const cache = new Map<string, FigureDef>();

export function scribblePose(style: StyleId, mood: ScribbleMood): FigureDef {
  const key = `${style}:${mood}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const base = scribble(style);
  const ey = style === 'C' ? 106 : 102;
  let layers = base.layers.slice();
  const mouthIdx = layers.findIndex((l) => l.k === 'ink' && l.d.startsWith('M120 134'));
  const swapEyes = (eyes: Layer[]) => {
    layers = layers.filter((l) => !isEye(l));
    const at = layers.findIndex((l) => l.k === 'spot' && l.fill === 'blush');
    layers.splice(at, 0, ...eyes);
  };
  const openMouth = (o = false): Layer => ({
    k: 'region',
    d: o ? ellipse(120, 142, 5.5, 6.5) : 'M108 136C112 149 128 149 132 136C125 139 115 139 108 136Z',
    fill: 'mouth',
    stage: 3,
    shade: false,
  });
  const dropMouth = () => {
    layers = layers.filter((l, i) => !(l.k === 'ink' && i >= mouthIdx - 1 && (l.d.startsWith('M120 134') || l.d.startsWith(M('M120 134').slice(0, 7)))));
  };

  /** Arms go under the scarf and head (inserted after the body), so the head overlaps the shoulders. */
  const behindHead = (arm: Layer[]) => {
    const at = layers.findIndex((l) => l.k === 'region' && l.fill === 'scarf');
    layers.splice(at, 0, ...arm);
  };
  if (mood === 'proud' || mood === 'cheer') {
    swapEyes(happyEyes(ey));
    dropMouth();
    layers.push(openMouth());
    behindHead([...foxArm(100, 178, 36, 136), ...foxArm(140, 178, 204, 136)]);
    layers.push(sparkle(40, 70, 9), sparkle(206, 62, 7, 'pink'), sparkle(214, 96, 5));
  } else if (mood === 'wave') {
    swapEyes(happyEyes(ey));
    behindHead(foxArm(142, 176, 206, 116));
    layers.push({ k: 'ink', d: 'M220 98C224 104 225 110 224 116', w: 0.7 }, { k: 'ink', d: 'M228 90C234 98 235 108 233 116', w: 0.7 });
  } else if (mood === 'surprised') {
    dropMouth();
    layers.push(openMouth(true));
    layers.push({ k: 'ink', d: 'M86 74C91 70 98 69 104 71', w: 0.9, color: 'foxDark' }, { k: 'ink', d: M('M86 74C91 70 98 69 104 71'), w: 0.9, color: 'foxDark' });
    layers.push(sparkle(52, 52, 6), sparkle(192, 46, 5));
  } else if (mood === 'sleepy') {
    swapEyes(happyEyes(ey + 3, -1));
    layers.push({ k: 'ink', d: 'M178 52L192 52L178 66L192 66', w: 0.8, taper: 'none' }, { k: 'ink', d: 'M198 30L207 30L198 39L207 39', w: 0.6, taper: 'none' });
  } else if (mood === 'thinking') {
    swapEyes([
      { k: 'spot', d: ellipse(100, ey - 2, 8.5, 11), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(103, ey - 8, 3.4, 3.4), fill: 'white', stage: 3 },
      { k: 'spot', d: ellipse(144, ey - 2, 8.5, 11), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(147, ey - 8, 3.4, 3.4), fill: 'white', stage: 3 },
    ]);
    layers.push(...foxArm(100, 178, 112, 150));
    layers.push({ k: 'spot', d: ellipse(196, 64, 5), fill: 'white' }, { k: 'spot', d: ellipse(208, 46, 7.5), fill: 'white' }, { k: 'spot', d: ellipse(222, 24, 11), fill: 'white' });
    layers.push({ k: 'ink', d: ellipse(222, 24, 11).replace(/Z$/, ''), w: 0.45, taper: 'none' }, { k: 'ink', d: ellipse(208, 46, 7.5).replace(/Z$/, ''), w: 0.4, taper: 'none' });
  } else if (mood === 'point') {
    swapEyes([
      { k: 'spot', d: ellipse(102, ey, 8.5, 11), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(105, ey - 4, 3.4, 3.4), fill: 'white', stage: 3 },
      { k: 'spot', d: ellipse(146, ey, 8.5, 11), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(149, ey - 4, 3.4, 3.4), fill: 'white', stage: 3 },
    ]);
    behindHead(foxArm(142, 178, 208, 160));
  } else if (mood === 'curious') {
    layers = layers.map((l, i) => (i >= HEAD_FROM && l.k !== 'region' ? { ...l, d: rotate(l.d, -9, 120, 150) } : i >= HEAD_FROM && l.k === 'region' && l.fill !== 'scarf' ? { ...l, d: rotate(l.d, -9, 120, 150), ...(l.clip ? { clip: rotate(l.clip, -9, 120, 150) } : {}) } : l));
    layers.push({ k: 'ink', d: 'M196 40C200 30 212 30 212 40C212 48 204 49 204 56', w: 0.9, taper: 'none', color: 'foxTip' }, { k: 'spot', d: ellipse(204, 64, 2.6), fill: 'foxTip' });
  }
  const def: FigureDef = { ...base, layers, guides: [] };
  cache.set(key, def);
  return def;
}

/* ----------------------------------- Juno ----------------------------------- */

const skinHand = (x: number, y: number): Layer[] => [{ k: 'region', d: ellipse(x, y, 13, 12), fill: 'skin', shade: 4 }];

export function junoPose(style: StyleId, face: JunoFace): FigureDef {
  const key = `J${style}:${face}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const base = juno(style);
  const ky = style === 'C' ? 118 : 116;
  let layers = base.layers.slice();
  const isJEye = (l: Layer) => l.k === 'spot' && (l.fill === 'eye' || l.fill === 'white') && l.d.startsWith('M') && Math.abs(Number(l.d.slice(1).split(' ')[1]) - ky) < 14;
  const mouthAt = layers.findIndex((l) => l.k === 'region' && l.fill === 'mouth');
  const setMouth = (d: string) => {
    layers[mouthAt] = { k: 'region', d, fill: 'mouth', stage: 3, shade: false };
  };
  const swapEyes = (eyes: Layer[]) => {
    const at = layers.findIndex(isJEye);
    layers = layers.filter((l) => !isJEye(l));
    layers.splice(at, 0, ...eyes);
  };
  const closed = (y: number) => [
    { k: 'ink', d: `M92 ${y + 3}C96 ${y - 5} 106 ${y - 5} 110 ${y + 3}`, w: 1.05 } as Layer,
    { k: 'ink', d: `M130 ${y + 3}C134 ${y - 5} 144 ${y - 5} 148 ${y + 3}`, w: 1.05 } as Layer,
  ];
  const pencil = (x: number, y: number, deg: number): Layer[] => [
    { k: 'region', d: rotate(`M${x - 4} ${y}L${x + 4} ${y}L${x + 4} ${y - 40}L${x - 4} ${y - 40}Z`, deg, x, y), fill: 'pencil', shade: false },
    { k: 'region', d: rotate(`M${x - 4} ${y - 40}L${x + 4} ${y - 40}L${x} ${y - 50}Z`, deg, x, y), fill: 'pencilWood', shade: false },
  ];

  if (face === 'laugh' || face === 'proud' || face === 'cheer') {
    swapEyes(closed(ky));
    setMouth('M106 137C110 154 130 154 134 137C125 141 115 141 106 137Z');
  }
  if (face === 'wow') {
    setMouth(ellipse(120, 143, 7, 8));
    layers.push({ k: 'spot', d: 'M40 70l3 7 7 3-7 3-3 7-3-7-7-3 7-3z', fill: 'bell' }, { k: 'spot', d: 'M196 60l2.4 5.6 5.6 2.4-5.6 2.4-2.4 5.6-2.4-5.6-5.6-2.4 5.6-2.4z', fill: 'pink' });
  }
  if (face === 'thinking') {
    swapEyes([
      { k: 'spot', d: ellipse(103, ky - 3, 6.5, 8.5), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(105.5, ky - 7, 2.6, 2.6), fill: 'white', stage: 3 },
      { k: 'spot', d: ellipse(141, ky - 3, 6.5, 8.5), fill: 'eye', stage: 3 },
      { k: 'spot', d: ellipse(143.5, ky - 7, 2.6, 2.6), fill: 'white', stage: 3 },
    ]);
    setMouth('M112 142C116 139 126 139 130 143C125 145 117 145 112 142Z');
    layers.push(...skinHand(98, 168), ...pencil(98, 160, -18));
  }
  if (face === 'point') {
    layers.push({ k: 'region', d: capsule(172, 228, 200, 172, 14), fill: 'sweater', shade: 4 }, ...skinHand(202, 162), ...pencil(203, 154, 28));
  }
  if (face === 'cheer' || face === 'proud') {
    layers.push({ k: 'region', d: capsule(70, 226, 42, 170, 14), fill: 'sweater', shade: 4 }, ...skinHand(40, 160), { k: 'region', d: capsule(170, 226, 198, 170, 14), fill: 'sweater', shade: 4 }, ...skinHand(200, 160));
    layers.push(sparkle(30, 100, 8, 'bell'), sparkle(212, 96, 7, 'pink'));
  }
  const def: FigureDef = { ...base, layers, guides: [] };
  cache.set(key, def);
  return def;
}
